export interface SignUpGeoRestrictionNewsletterConfig {
  enabled?: unknown;
  labelHtml?: string;
  /** Destination when the user clicks the subscribe button (relative path or http(s) URL). */
  subscribeUrl?: string;
  buttonLabel?: string;
}

export interface SignUpGeoRestrictionConfig {
  enabled?: unknown;
  countryFieldName?: string;
  allowedCountryCodes?: string[];
  applyToUseCaseSlugs?: string[];
  applyWhenNotForBusinesses?: unknown;
  /** Path within the auth SPA (e.g. `/region-unavailable`). */
  unavailablePagePath?: string;
  bannerHtml?: string;
  newsletter?: SignUpGeoRestrictionNewsletterConfig;
}

export interface NormalizedSignUpGeoRestrictionNewsletterConfig {
  enabled: boolean;
  labelHtml?: string;
  subscribeUrl?: string;
  buttonLabel: string;
}

export interface NormalizedSignUpGeoRestrictionConfig {
  enabled: boolean;
  countryFieldName: string;
  allowedCountryCodes: string[];
  applyToUseCaseSlugs: string[];
  applyWhenNotForBusinesses: boolean;
  unavailablePagePath: string;
  bannerHtml?: string;
  newsletter: NormalizedSignUpGeoRestrictionNewsletterConfig;
}

function parseFlag(value: unknown, defaultValue: boolean): boolean {
  if (value === undefined || value === null) return defaultValue;
  if (value === false || value === 0) return false;
  if (value === true || value === 1) return true;
  if (typeof value === "string") {
    const t = value.trim().toLowerCase();
    if (t === "false" || t === "0" || t === "no") return false;
    if (t === "true" || t === "1" || t === "yes") return true;
  }
  return defaultValue;
}

export function normalizeSignUpGeoRestriction(
  raw?: SignUpGeoRestrictionConfig | null
): NormalizedSignUpGeoRestrictionConfig {
  const allowedCountryCodes = Array.isArray(raw?.allowedCountryCodes)
    ? raw.allowedCountryCodes.map(code => String(code).trim().toUpperCase()).filter(Boolean)
    : [];
  const applyToUseCaseSlugs = Array.isArray(raw?.applyToUseCaseSlugs)
    ? raw.applyToUseCaseSlugs.map(slug => String(slug).trim()).filter(Boolean)
    : [];

  return {
    enabled: parseFlag(raw?.enabled, false),
    countryFieldName:
      typeof raw?.countryFieldName === "string" && raw.countryFieldName.trim()
        ? raw.countryFieldName.trim()
        : "countryOfResidence",
    allowedCountryCodes,
    applyToUseCaseSlugs,
    applyWhenNotForBusinesses: parseFlag(raw?.applyWhenNotForBusinesses, false),
    unavailablePagePath:
      typeof raw?.unavailablePagePath === "string" && raw.unavailablePagePath.trim()
        ? raw.unavailablePagePath.trim()
        : "/region-unavailable",
    bannerHtml: typeof raw?.bannerHtml === "string" && raw.bannerHtml.trim() ? raw.bannerHtml : undefined,
    newsletter: {
      enabled: parseFlag(raw?.newsletter?.enabled, true),
      labelHtml:
        typeof raw?.newsletter?.labelHtml === "string" && raw.newsletter.labelHtml.trim()
          ? raw.newsletter.labelHtml
          : undefined,
      subscribeUrl:
        typeof raw?.newsletter?.subscribeUrl === "string" && raw.newsletter.subscribeUrl.trim()
          ? raw.newsletter.subscribeUrl.trim()
          : undefined,
      buttonLabel:
        typeof raw?.newsletter?.buttonLabel === "string" && raw.newsletter.buttonLabel.trim()
          ? raw.newsletter.buttonLabel.trim()
          : "Subscribe to newsletter",
    },
  };
}

export function resolveSignUpGeoRestrictionBannerHtml(bannerHtml: string | undefined): string | null {
  if (!bannerHtml?.trim()) return null;
  return bannerHtml;
}

/** Validates newsletter subscribe targets: relative paths or http(s) URLs only. */
export function resolveSignUpGeoNewsletterSubscribeUrl(url: string | undefined): string | null {
  if (!url?.trim()) return null;
  const trimmed = url.trim();
  if (trimmed.startsWith("//")) return null;
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmed)) {
    try {
      const parsed = new URL(trimmed);
      if (parsed.protocol === "https:" || parsed.protocol === "http:") {
        return parsed.href;
      }
    } catch {
      return null;
    }
    return null;
  }
  if (trimmed.startsWith("/")) return trimmed;
  return null;
}
